import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldAlert, Swords, Boxes, ArrowRight, Sparkles, Lock } from "lucide-react";
import { useAppVersion } from "@/hooks/useAppVersion";
import { AgeGate } from "@/components/AgeGate";
import { Switch } from "@/components/ui/switch";

/**
 * Portal / chooser page — the very first thing visitors see at `/`.
 * Splits Playden Studios into its two products:
 *   • The Playden RPG card platform (NSFW · 21+)
 *   • Playden 3D Printing (mainstream · all-ages)
 * No sticky memory — every visit shows the chooser so people always know
 * where they are and can hop between the two experiences deliberately.
 */
export default function PortalPage() {
  const [entered, setEntered] = useState(false);
  const tosVersion = useAppVersion();
  const navigate = useNavigate();
  // Age-gate for the Playden Classic card. Intentionally NOT persisted —
  // every visit to the portal re-locks the card, so the user has to
  // re-affirm they're 21+ on every session. Legal shield stays strong;
  // no localStorage carry-over between visits.
  const [classicUnlocked, setClassicUnlocked] = useState(false);
  // When the user clicks "I confirm I am 21 or over" on the Classic card
  // we no longer instantly unlock — instead we surface the full 5-point
  // <AgeGate> modal. Only after they complete the real acknowledgment
  // (including the affirmations that all subjects are 21+, consensual,
  // lawful, and that they'll indemnify Playden) does the card unlock and
  // the router navigate them into Playden Classic. This is what the user
  // reported as broken: a single click was enough to enter — no more.
  const [showFullGate, setShowFullGate] = useState(false);
  // NSFW (Playden Classic) is hidden by default — the visitor must flip this
  // toggle to even reveal the adult side. Default focus is the all-ages 3D
  // Printing storefront. Not persisted: every visit starts safe/off.
  const [showNsfw, setShowNsfw] = useState(false);
  const requestClassicEntry = () => setShowFullGate(true);
  const onGateAccept = () => {
    setShowFullGate(false);
    setClassicUnlocked(true);
    // Straight into Playden Classic. Downstream gates on `/home`
    // (How-To/Rules, ToS acceptance) will still fire — this is only the
    // adult-content acknowledgment.
    navigate("/home");
  };
  const onGateLeave = () => {
    // "Under 21 / I don't agree" — bounce them off the property.
    try { window.location.href = "https://www.google.com"; } catch (_) { /* noop */ }
  };

  useEffect(() => {
    const t = setTimeout(() => setEntered(true), 40);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      data-testid="portal-page"
      className="min-h-screen relative overflow-hidden flex items-center justify-center px-4 py-16"
    >
      {/* Ambient background — the same cinematic Playden palette (kept here
          because Portal doesn't render inside the app Layout). */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(1200px_600px_at_20%_10%,rgba(34,211,238,0.10),transparent),radial-gradient(1000px_500px_at_80%_90%,rgba(232,121,249,0.10),transparent)]" />
        <div className="absolute inset-0 bg-[hsl(222_22%_5%)]" style={{ mixBlendMode: "multiply" }} />
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "repeating-linear-gradient(0deg, rgba(255,255,255,0.6) 0, rgba(255,255,255,0.6) 1px, transparent 1px, transparent 3px)" }} />
      </div>

      <div className={`relative w-full max-w-6xl transition-all duration-700 ${entered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        {/* Brand mark */}
        <div className="flex flex-col items-center text-center gap-3 mb-12">
          <div className="relative h-20 w-20 rounded-2xl overflow-hidden bg-black shadow-[0_0_38px_rgba(0,220,255,0.55)] border border-white/15">
            <img src="/brand/playden-black.png" alt="Playden Studios" className="absolute inset-0 w-full h-full object-contain p-1" />
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-cyan-300/80">Playden Studios</span>
            <span className="inline-block h-[3px] w-[3px] rounded-full bg-cyan-300/60" />
            <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-white/40">Portal</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-[0.06em] text-white mt-1">
            CHOOSE YOUR SIDE.
          </h1>
          <p className="max-w-xl text-white/55 text-sm leading-relaxed">
            Step into our 3D Printing studio below. Adult collectors can flip the switch to unlock Playden Classic (NSFW · 21+).
          </p>
        </div>

        {/* NSFW reveal toggle — off by default so the adult side never shows unprompted. */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <Switch
            data-testid="portal-nsfw-toggle"
            checked={showNsfw}
            onCheckedChange={setShowNsfw}
            className="data-[state=checked]:bg-red-500"
          />
          <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/60">
            Show Playden Classic — Digital Card RPG · <span className="text-red-300">NSFW · 21+</span>
          </span>
        </div>

        {/* Chooser — 3D Printing is always shown; the NSFW card only appears once toggled. */}
        <div className={showNsfw ? "grid md:grid-cols-2 gap-6 lg:gap-8" : "max-w-xl mx-auto"}>
          <PortalCard
            testid="portal-choice-prints"
            to="/prints"
            title="PLAYDEN 3D PRINTING"
            kicker="Prints · Makers · All ages"
            tagline="Physical 3D-printed collectibles — upload your own files or order from our catalog."
            icon={Boxes}
            accent="#f472b6"
            highlights={[
              "Bring-your-own STL or OBJ",
              "Playden card art figures & minis",
              "Ships worldwide (coming soon)",
            ]}
            warning="Mainstream storefront. No adult gating unless required."
          />
          {showNsfw && (
            <div className="relative group" data-testid="portal-classic-wrapper">
              {/* Banner clarifying what Playden Classic actually is. */}
              <div className="mb-2 rounded-lg border border-cyan-300/30 bg-cyan-400/5 px-3 py-2 text-center" data-testid="portal-classic-banner">
                <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-cyan-200">Digital Card-Collecting RPG · Fantasy Game</span>
              </div>
              <PortalCard
                testid="portal-choice-playden"
                to="/home"
                title="PLAYDEN CLASSIC"
                versionTag={`v${tosVersion}`}
                kicker="Digital Card RPG · Fantasy · NSFW 21+"
                tagline="Collect serialized cards, raid caverns, battle in the arena, trade & compete — a digital card-collecting fantasy RPG."
                icon={Swords}
                accent="#22d3ee"
                highlights={[
                  "Holographic serialized collectible cards",
                  "PvP Arena · PvE Cavern raids",
                  "Trades, races, adventure & gear",
                ]}
                warning="Adult content. Age-gated. Not for the fragile."
                locked={!classicUnlocked}
              />
              {!classicUnlocked && (
                <ClassicAgeLock onConfirm={requestClassicEntry} />
              )}
            </div>
          )}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3 text-white/40">
          <Sparkles className="h-3.5 w-3.5 text-cyan-300/70" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em]">One brand · Two experiences · Zero compromise</span>
        </div>
      </div>
      {/* Real 5-point age gate — only mounts once the visitor clicks the
          "I confirm I am 21 or over" button on the Classic card. Forces a
          fresh acknowledgment even for returning visitors so the Portal is
          never bypassed by a stale localStorage flag. */}
      {showFullGate && (
        <AgeGate forceOpen onAccept={onGateAccept} onLeave={onGateLeave} />
      )}
    </div>
  );
}

function PortalCard({ testid, to, title, versionTag, kicker, tagline, icon: Icon, accent, highlights, warning, locked = false }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      aria-disabled={locked || undefined}
      tabIndex={locked ? -1 : 0}
      onClick={locked ? (e) => e.preventDefault() : undefined}
      className={`group relative block rounded-3xl border border-white/10 bg-[hsl(222_22%_7%)]/80 backdrop-blur-md p-8 transition-all duration-500 overflow-hidden ${
        locked ? "cursor-default select-none" : "hover:-translate-y-1 hover:border-white/20"
      }`}
      style={{ boxShadow: `0 20px 60px rgba(0,0,0,0.55)` }}
    >
      {/* Neon glow ring that intensifies on hover */}
      <div
        className="pointer-events-none absolute inset-0 rounded-3xl opacity-40 group-hover:opacity-90 transition-opacity duration-500"
        style={{ boxShadow: `inset 0 0 0 1px ${accent}55, 0 0 60px ${accent}33` }}
      />
      {/* Scan line texture */}
      <div className="pointer-events-none absolute inset-0 opacity-10" style={{ backgroundImage: `repeating-linear-gradient(180deg, ${accent} 0, ${accent} 1px, transparent 1px, transparent 6px)` }} />

      <div className={`relative flex flex-col gap-5 transition-all duration-500 ${locked ? "blur-[10px] group-hover:blur-[6px] opacity-70 select-none" : ""}`}>
        <div className="flex items-start justify-between">
          <div className="h-14 w-14 rounded-2xl flex items-center justify-center border" style={{ borderColor: `${accent}55`, background: `${accent}12` }}>
            <Icon className="h-7 w-7" style={{ color: accent }} />
          </div>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] px-2.5 py-1 rounded-full border" style={{ color: accent, borderColor: `${accent}55`, background: `${accent}10` }}>
            {kicker}
          </span>
        </div>

        <div>
          <div className="flex items-baseline gap-2.5 flex-wrap">
            <div className="font-display text-2xl sm:text-3xl tracking-[0.14em] text-white">{title}</div>
            {versionTag && (
              <span
                className="font-mono text-[10px] uppercase tracking-[0.24em] px-2 py-0.5 rounded-md border tabular-nums"
                style={{ color: accent, borderColor: `${accent}55`, background: `${accent}12` }}
                data-testid={`${testid}-version`}
              >
                {versionTag}
              </span>
            )}
          </div>
          <div className="mt-2 text-white/70 text-sm leading-relaxed">{tagline}</div>
        </div>

        <ul className="space-y-1.5">
          {highlights.map((h) => (
            <li key={h} className="flex items-center gap-2 text-xs text-white/60">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} />
              {h}
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2 text-[11px] text-amber-300/70 font-mono uppercase tracking-[0.18em]">
          <ShieldAlert className="h-3.5 w-3.5" />
          <span>{warning}</span>
        </div>

        <div className="mt-2 flex items-center justify-between">
          <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-white/45 group-hover:text-white transition-colors">Enter →</span>
          <ArrowRight className="h-5 w-5 text-white/40 group-hover:text-white group-hover:translate-x-1 transition-all" />
        </div>
      </div>
    </Link>
  );
}

/** Age-verification overlay that sits on top of the Playden Classic card.
 *  Blurs the card underneath, shows the legal warning, and requires an
 *  explicit click-to-confirm before the card becomes navigable. Persists
 *  the acceptance in localStorage so we don't nag on every visit. */
function ClassicAgeLock({ onConfirm }) {
  return (
    <div
      data-testid="portal-classic-lock"
      className="absolute inset-0 z-20 rounded-3xl overflow-hidden flex items-center justify-center p-6 sm:p-8"
    >
      {/* Backdrop — heavy on rest, lightens on hover so the underlying
          card starts to reveal itself. Pure CSS, no state. */}
      <div className="absolute inset-0 bg-black/75 backdrop-blur-md transition-all duration-500 group-hover:bg-black/55 group-hover:backdrop-blur-[6px]" />
      {/* Warning stripes — subtle diagonal caution tape */}
      <div
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{ backgroundImage: "repeating-linear-gradient(45deg, rgba(245,158,11,0.6) 0, rgba(245,158,11,0.6) 12px, transparent 12px, transparent 32px)" }}
      />

      <div className="relative w-full max-w-sm text-center flex flex-col items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.28em] px-2.5 py-1 rounded-md border border-red-400/60 bg-red-500/15 text-red-200 shadow-[0_0_18px_rgba(239,68,68,0.4)]">
            NSFW
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.28em] px-2.5 py-1 rounded-md border border-amber-300/50 bg-amber-400/15 text-amber-100">
            21+ Adult
          </span>
        </div>
        <div className="h-14 w-14 rounded-2xl border border-amber-300/50 bg-amber-400/15 flex items-center justify-center shadow-[0_0_28px_rgba(245,158,11,0.5)]">
          <Lock className="h-7 w-7 text-amber-300" />
        </div>
        <div>
          <div className="font-display text-2xl sm:text-3xl tracking-[0.14em] text-white leading-tight">
            NSFW · 21+ ONLY
          </div>
          <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.32em] text-amber-300">
            No minors permitted
          </div>
        </div>
        <p className="text-[12px] leading-relaxed text-white/70">
          Playden Classic contains <span className="font-semibold text-white">explicit NSFW adult content</span>. This side is adults-only. Violators will be prosecuted to the fullest extent of the law.
        </p>
        <button
          type="button"
          onClick={onConfirm}
          data-testid="portal-classic-age-confirm"
          className="mt-1 w-full rounded-xl border border-amber-300/60 bg-amber-400/15 hover:bg-amber-400/25 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.24em] text-amber-100 hover:text-white transition-all shadow-[0_0_28px_rgba(245,158,11,0.35)] hover:shadow-[0_0_36px_rgba(245,158,11,0.55)]"
        >
          Continue — verify age &amp; agree
        </button>
        <div className="font-mono text-[9px] uppercase tracking-[0.28em] text-white/35 flex items-center gap-2">
          <ShieldAlert className="h-3 w-3 text-amber-300/70" />
          Full 21+ acknowledgment required to enter
        </div>
      </div>
    </div>
  );
}
